import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Form,
  Input,
  Button,
  Upload,
  Select,
  Card,
  Typography,
  Divider,
  Row,
  Col,
  message,
} from "antd";
import {
  BankOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  LockOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { fetchDistricts } from "../../api/api";
import authApi from "../../api/authApi";
import api from "../../api/config";
import { prepareFormData, validateFileType, validateFileSize } from "../../utils/formDataHelper";

const { Title, Text } = Typography;
const { Option } = Select;

const AcademiaRegister = ({ onSuccess, isModal = false, editingAcademia = null, isAdminEdit = false }) => {
  const [form] = Form.useForm();
  const [formData, setFormData] = useState({
    collegeName: "",
    principalName: "",
    contactPerson: "",
    email: "",
    phone: "",
    district: "",
    address: "",
    password: "",
    password2: "",
    logo: null,
    latitude: 20.9374,
    longitude: 77.7796,
  });
  const [loading, setLoading] = useState(false);
  const [logoFileList, setLogoFileList] = useState([]);
  const [logoPreview, setLogoPreview] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const {
    collegeName,
    principalName,
    contactPerson,
    email,
    phone,
    district,
    address,
    password,
    password2,
  } = formData;

  const baseStyle = {
    fontFamily: "Rubik, sans-serif",
    fontSize: "14px",
    fontWeight: 400,
    color: "#333",
  };

  const inputStyle = {
    fontFamily: "Rubik, sans-serif",
    fontSize: "15px",
    borderRadius: "6px",
  };

  const labelStyle = {
    fontFamily: "Rubik, sans-serif",
    fontSize: "16px",
    fontWeight: 400,
    color: "#333",
    marginBottom: "6px",
  };

  useEffect(() => {
    const loadDistricts = async () => {
      try {
        const response = await fetchDistricts();
        
        if (Array.isArray(response.data)) {
          console.log("First district object:", response.data[0]);
          setDistricts(response.data);
        } else if (Array.isArray(response.districts)) {
          console.log("First district object:", response.districts[0]);
          setDistricts(response.districts);
        } else if (Array.isArray(response)) {
          console.log("First district object:", response[0]);
          setDistricts(response);
        } else {
          console.error("Unexpected districts data format:", response);
          setDistricts([]);
        }
      } catch (error) {
        console.error("Error fetching districts:", error);
        message.error("Failed to load districts");
        setDistricts([]);
      }
    };
    loadDistricts();
  }, []);

  // Populate form when editing
  useEffect(() => {
    if (editingAcademia) {
      form.setFieldsValue({
        collegeName: editingAcademia.schoolName || editingAcademia.collegeName,
        principalName: editingAcademia.principalName,
        contactPerson: editingAcademia.contactPerson,
        email: editingAcademia.email,
        phone: editingAcademia.phone,
        district: editingAcademia.district,
        address: editingAcademia.address,
      });

      setFormData(prev => ({
        ...prev,
        collegeName: editingAcademia.schoolName || editingAcademia.collegeName,
        principalName: editingAcademia.principalName,
        contactPerson: editingAcademia.contactPerson,
        email: editingAcademia.email,
        phone: editingAcademia.phone,
        district: editingAcademia.district,
        address: editingAcademia.address,
        latitude: editingAcademia.latitude || 20.9374,
        longitude: editingAcademia.longitude || 77.7796,
      }));

      // Set logo preview if exists
      if (editingAcademia.logo) {
        const logoUrl = editingAcademia.logo.startsWith('http') 
          ? editingAcademia.logo 
          : `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}${editingAcademia.logo.startsWith('/') ? '' : '/'}${editingAcademia.logo}`;
        setLogoPreview(logoUrl);
        setLogoFileList([{
          uid: '-1',
          name: 'logo.png',
          status: 'done',
          url: logoUrl,
        }]);
      }
    }
  }, [editingAcademia, form]);

  const handleChange = (changedValues, allValues) => {
    // Update formData state when form values change
    setFormData(prevData => ({
      ...prevData,
      ...allValues
    }));
    
    // Clear errors for changed fields
    const changedFieldNames = Object.keys(changedValues);
    if (changedFieldNames.length > 0) {
      setErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        changedFieldNames.forEach(name => {
          delete newErrors[name];
        });
        return newErrors;
      });
    }
  };

  const logoUploadProps = {
    beforeUpload: (file) => {
      if (!validateFileType(file, ['image/jpeg', 'image/png', 'image/gif'])) {
        message.error('Only JPG, PNG or GIF files are allowed');
        return Upload.LIST_IGNORE;
      }
      if (!validateFileSize(file, 5)) {
        message.error('File size should be less than 5MB');
        return Upload.LIST_IGNORE;
      }
      
      setFormData(prev => ({ ...prev, logo: file }));
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      return false; // Prevent auto upload
    },
    onChange: ({ fileList }) => setLogoFileList(fileList.slice(-1)),
    fileList: logoFileList,
    listType: "picture-card",
    maxCount: 1,
  };

  const handleLocationSelect = (lat, lng) => {
    setFormData({
      ...formData,
      latitude: lat,
      longitude: lng,
    });
  };
  
  const handleBack = () => {
    navigate(-1);
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    
    try {
      const dataToSend = {
        collegeName: values.collegeName,
        principalName: values.principalName,
        contactPerson: values.contactPerson,
        email: values.email,
        phone: values.phone,
        district: values.district,
        address: values.address,
        latitude: formData.latitude,
        longitude: formData.longitude,
        logo: formData.logo,
        schoolName: values.collegeName, // Map collegeName to schoolName for the API
      };

      // Only include password for new registrations
      if (!editingAcademia) {
        dataToSend.password = values.password;
        dataToSend.password2 = values.password2;
      }

      // Prepare form data for file upload
      const formDataToSend = prepareFormData(dataToSend);
      
      if (editingAcademia) {
        // Update existing academia
        const response = await api.put(`/admin/academia/${editingAcademia._id}`, formDataToSend);
        message.success("Academia updated successfully!");
        
        if (onSuccess) {
          onSuccess();
        }
      } else {
        // Register new academia
        const response = await authApi.registerAcademia(formDataToSend);
        message.success("Registration successful! Please log in.");
        
        if (isModal && onSuccess) {
          onSuccess();
        } else if (!isAdminEdit) {
          navigate("/login");
        }
      }
    } catch (error) {
      console.error("Registration error:", error);
      console.error("Error details:", error.errors);
      
      // Handle validation errors from the server
      if (error.errors) {
        console.log("Validation errors:", error.errors);
        const errorMsgs = error.errors.map(err => `${err.param}: ${err.msg}`).join("\n");
        message.error(errorMsgs);
      } else if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error(error.message || "Operation failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!collegeName.trim()) newErrors.collegeName = 'College/University name is required';
    if (!principalName.trim()) newErrors.principalName = 'Principal name is required';
    if (!contactPerson.trim()) newErrors.contactPerson = 'Contact person is required';
    if (!email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Email is invalid';
    if (!phone) newErrors.phone = 'Phone number is required';
    else if (!/^[0-9]{10}$/.test(phone)) newErrors.phone = 'Phone number must be 10 digits';
    if (!district) newErrors.district = 'District is required';
    if (!address) newErrors.address = 'Address is required';
    if (!password) newErrors.password = 'Password is required';
    else if (password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    if (password !== password2) newErrors.password2 = 'Passwords do not match';
    
    // File validations
    if (formData.logo) {
      if (!validateFileType(formData.logo, ['image/jpeg', 'image/png', 'image/gif'])) {
        newErrors.logo = 'Only JPG, PNG or GIF files are allowed';
      } else if (!validateFileSize(formData.logo, 5)) { // 5MB max
        newErrors.logo = 'File size should be less than 5MB';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <>
      <style>
        {`
          .ant-form-item {
            margin-bottom: 16px !important;
          }
          .ant-input,
          .ant-input-password input,
          .ant-select-selector {
            font-family: 'Rubik', sans-serif !important;
            font-size: 14px !important;
            color: #333 !important;
            height: 33px !important;
            line-height: 38px !important;
          }
          .ant-input-affix-wrapper {
            padding: 0 11px !important;
            height: 38px !important;
          }
          .ant-input-prefix {
            margin-right: 8px !important;
          }
          .ant-input::placeholder,
          .ant-input-password input::placeholder {
            font-family: 'Rubik', sans-serif !important;
            font-size: 14px !important;
            color: #bfbfbf !important;
            font-weight: 400 !important;
          }
          .ant-select-selection-placeholder {
            font-family: 'Rubik', sans-serif !important;
            font-size: 14px !important;
            color: #bfbfbf !important;
            font-weight: 400 !important;
            line-height: 36px !important;
          }
          .ant-select-single .ant-select-selector {
            height: 38px !important;
            padding: 0 11px !important;
          }
          .ant-select-selection-search-input {
            height: 36px !important;
          }
          .ant-input-textarea textarea {
            font-family: 'Rubik', sans-serif !important;
            font-size: 14px !important;
            color: #333 !important;
          }
        `}
      </style>

      {isModal ? (
        // Modal content - no wrapper
        <div style={{ fontFamily: "Rubik, sans-serif" }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onValuesChange={handleChange}
            initialValues={formData}
          >
            {/* Institution Details Section */}
            <Title
              level={4}
              style={{
                ...baseStyle,
                fontSize: "21px",
                fontWeight: 500,
                marginBottom: "16px",
                color: "#470172",
                textAlign: "left",
              }}
            >
              <BankOutlined style={{ marginRight: "8px" }} />
              Institution Details
            </Title>

            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item
                  label={<span style={labelStyle}>College/University Name</span>}
                  name="collegeName"
                  rules={[{ required: true, message: "Please enter college/university name" }]}
                >
                  <Input
                    prefix={<BankOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                    placeholder="Enter your institution name"
                    size="middle"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={labelStyle}>Principal's Name</span>}
                  name="principalName"
                  rules={[{ required: true, message: "Please enter principal's name" }]}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                    placeholder="Enter principal's full name"
                    size="middle"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={labelStyle}>Contact Person</span>}
                  name="contactPerson"
                  rules={[{ required: true, message: "Please enter contact person name" }]}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                    placeholder="Enter contact person name"
                    size="middle"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item label={<span style={labelStyle}>Institution Logo</span>}>
                  <Upload {...logoUploadProps}>
                    {logoFileList.length < 1 && (
                      <div style={{ padding: "12px", textAlign: "center" }}>
                        <PlusOutlined style={{ fontSize: "20px", color: "#470172" }} />
                        <div style={{ ...baseStyle, fontSize: "12px", marginTop: "6px", color: "#666" }}>
                          Click to upload logo
                        </div>
                      </div>
                    )}
                  </Upload>
                  <Text style={{ ...baseStyle, fontSize: "12px", color: "#999", marginTop: "4px" }}>
                    Square format (512x512px), Max 5MB
                  </Text>
                </Form.Item>
              </Col>
            </Row>

            <Divider
              style={{
                margin: "20px 0",
                borderColor: "#e0e0e0",
                borderStyle: "dotted",
                borderWidth: "1px",
              }}
            />

            {/* Contact Information Section */}
            <Title
              level={4}
              style={{
                ...baseStyle,
                fontSize: "21px",
                fontWeight: 500,
                marginBottom: "16px",
                color: "#470172",
                textAlign: "left",
              }}
            >
              <MailOutlined style={{ marginRight: "8px" }} />
              Contact Information
            </Title>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={labelStyle}>Email Address</span>}
                  name="email"
                  rules={[
                    { required: true, message: "Please enter email" },
                    { type: "email", message: "Please enter valid email" },
                  ]}
                >
                  <Input
                    prefix={<MailOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                    placeholder="institution@example.com"
                    size="middle"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={labelStyle}>Phone Number</span>}
                  name="phone"
                  rules={[
                    { required: true, message: "Please enter phone number" },
                    { pattern: /^[0-9]{10}$/, message: "Phone number must be 10 digits" },
                  ]}
                >
                  <Input
                    prefix={<PhoneOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                    placeholder="+91 98765 43210"
                    size="middle"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={labelStyle}>District</span>}
                  name="district"
                  rules={[{ required: true, message: "Please select district" }]}
                >
                  <Select
                    placeholder="Select district"
                    size="middle"
                    style={inputStyle}
                    suffixIcon={<EnvironmentOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                  >
                    {districts.map((d) => (
                      <Option key={d._id} value={d.districtName}>
                        {d.districtName}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  label={<span style={labelStyle}>College Address</span>}
                  name="address"
                  rules={[{ required: true, message: "Please enter address" }]}
                >
                  <Input.TextArea
                    rows={3}
                    placeholder="Enter complete institution address"
                    style={{ ...inputStyle, height: 'auto' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider
              style={{
                margin: "20px 0",
                borderColor: "#e0e0e0",
                borderStyle: "dotted",
                borderWidth: "1px",
              }}
            />

            {/* Security Section */}
            <Title
              level={4}
              style={{
                ...baseStyle,
                fontSize: "21px",
                fontWeight: 500,
                marginBottom: "16px",
                color: "#470172",
                textAlign: "left",
              }}
            >
              <LockOutlined style={{ marginRight: "8px" }} />
              Security
            </Title>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={labelStyle}>Password</span>}
                  name="password"
                  rules={[
                    { required: true, message: "Please enter password" },
                    { min: 6, message: "Password must be at least 6 characters" },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                    placeholder="Enter password"
                    size="middle"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={labelStyle}>Confirm Password</span>}
                  name="password2"
                  dependencies={['password']}
                  rules={[
                    { required: true, message: "Please confirm password" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Passwords do not match'));
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                    placeholder="Confirm password"
                    size="middle"
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider style={{ margin: "30px 0" }} />

            {/* Submit Button */}
            <Form.Item style={{ textAlign: "center" }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                style={{
                  backgroundColor: "#672f89",
                  borderColor: "#672f89",
                  height: "50px",
                  fontSize: "16px",
                  fontWeight: 500,
                  fontFamily: "Rubik, sans-serif",
                  borderRadius: "8px",
                  width: "300px",
                }}
              >
                {loading ? "Processing..." : "Register Institution"}
              </Button>
            </Form.Item>

            {!isModal && (
              <div style={{ textAlign: "center", marginTop: "20px" }}>
                <Text style={{ ...baseStyle, fontSize: "14px", color: "#666" }}>
                  Already have an account?{" "}
                  <a
                    href="/login"
                    style={{
                      color: "#672f89",
                      fontWeight: 500,
                      textDecoration: "none",
                    }}
                  >
                    Sign in
                  </a>
                </Text>
              </div>
            )}
          </Form>
        </div>
      ) : (
        // Standalone page content
        <div
          style={{
            backgroundColor: "#f5f5f5",
            fontFamily: "Rubik, sans-serif",
            minHeight: "100vh",
            padding: "40px 20px",
          }}
        >
          <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              style={{
                marginBottom: "20px",
                fontFamily: "Rubik, sans-serif",
                fontSize: "14px",
                color: "#470172",
                border: "1px solid #470172",
              }}
            >
              Back
            </Button>

            <Card
              style={{
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                borderRadius: "8px",
                border: "1px solid #e0e0e0",
              }}
              bodyStyle={{ padding: "40px" }}
            >
              <div style={{ textAlign: "center", marginBottom: "30px" }}>
                <BankOutlined style={{ fontSize: "48px", color: "#470172" }} />
                <Title
                  level={2}
                  style={{
                    ...baseStyle,
                    fontSize: "28px",
                    fontWeight: 500,
                    marginTop: "16px",
                    marginBottom: "8px",
                    color: "#470172",
                  }}
                >
                  Academia Registration
                </Title>
                <Text style={{ ...baseStyle, fontSize: "15px", color: "#666" }}>
                  Register your educational institution to join our network
                </Text>
              </div>

              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                onValuesChange={handleChange}
                initialValues={formData}
              >
                {/* Institution Details Section */}
                <Title
                  level={4}
                  style={{
                    ...baseStyle,
                    fontSize: "21px",
                    fontWeight: 500,
                    marginBottom: "16px",
                    color: "#470172",
                    textAlign: "left",
                  }}
                >
                  <BankOutlined style={{ marginRight: "8px" }} />
                  Institution Details
                </Title>

                <Row gutter={16}>
                  <Col xs={24}>
                    <Form.Item
                      label={<span style={labelStyle}>College/University Name</span>}
                      name="collegeName"
                      rules={[{ required: true, message: "Please enter college/university name" }]}
                    >
                      <Input
                        prefix={<BankOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                        placeholder="Enter your institution name"
                        size="middle"
                        style={inputStyle}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={labelStyle}>Principal's Name</span>}
                      name="principalName"
                      rules={[{ required: true, message: "Please enter principal's name" }]}
                    >
                      <Input
                        prefix={<UserOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                        placeholder="Enter principal's full name"
                        size="middle"
                        style={inputStyle}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={labelStyle}>Contact Person</span>}
                      name="contactPerson"
                      rules={[{ required: true, message: "Please enter contact person name" }]}
                    >
                      <Input
                        prefix={<UserOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                        placeholder="Enter contact person name"
                        size="middle"
                        style={inputStyle}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <Form.Item label={<span style={labelStyle}>Institution Logo</span>}>
                      <Upload {...logoUploadProps}>
                        {logoFileList.length < 1 && (
                          <div style={{ padding: "12px", textAlign: "center" }}>
                            <PlusOutlined style={{ fontSize: "20px", color: "#470172" }} />
                            <div style={{ ...baseStyle, fontSize: "12px", marginTop: "6px", color: "#666" }}>
                              Click to upload logo
                            </div>
                          </div>
                        )}
                      </Upload>
                      <Text style={{ ...baseStyle, fontSize: "12px", color: "#999", marginTop: "4px" }}>
                        Square format (512x512px), Max 5MB
                      </Text>
                    </Form.Item>
                  </Col>
                </Row>

                <Divider
                  style={{
                    margin: "20px 0",
                    borderColor: "#e0e0e0",
                    borderStyle: "dotted",
                    borderWidth: "1px",
                  }}
                />

                {/* Contact Information Section */}
                <Title
                  level={4}
                  style={{
                    ...baseStyle,
                    fontSize: "21px",
                    fontWeight: 500,
                    marginBottom: "16px",
                    color: "#470172",
                    textAlign: "left",
                  }}
                >
                  <MailOutlined style={{ marginRight: "8px" }} />
                  Contact Information
                </Title>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={labelStyle}>Email Address</span>}
                      name="email"
                      rules={[
                        { required: true, message: "Please enter email" },
                        { type: "email", message: "Please enter valid email" },
                      ]}
                    >
                      <Input
                        prefix={<MailOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                        placeholder="institution@example.com"
                        size="middle"
                        style={inputStyle}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={labelStyle}>Phone Number</span>}
                      name="phone"
                      rules={[
                        { required: true, message: "Please enter phone number" },
                        { pattern: /^[0-9]{10}$/, message: "Phone number must be 10 digits" },
                      ]}
                    >
                      <Input
                        prefix={<PhoneOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                        placeholder="+91 98765 43210"
                        size="middle"
                        style={inputStyle}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={labelStyle}>District</span>}
                      name="district"
                      rules={[{ required: true, message: "Please select district" }]}
                    >
                      <Select
                        placeholder="Select district"
                        size="middle"
                        style={inputStyle}
                        suffixIcon={<EnvironmentOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                      >
                        {districts.map((d) => (
                          <Option key={d._id} value={d.districtName}>
                            {d.districtName}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <Form.Item
                      label={<span style={labelStyle}>College Address</span>}
                      name="address"
                      rules={[{ required: true, message: "Please enter address" }]}
                    >
                      <Input.TextArea
                        rows={3}
                        placeholder="Enter complete institution address"
                        style={{ ...inputStyle, height: 'auto' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider
                  style={{
                    margin: "20px 0",
                    borderColor: "#e0e0e0",
                    borderStyle: "dotted",
                    borderWidth: "1px",
                  }}
                />

                {/* Security Section */}
                <Title
                  level={4}
                  style={{
                    ...baseStyle,
                    fontSize: "21px",
                    fontWeight: 500,
                    marginBottom: "16px",
                    color: "#470172",
                    textAlign: "left",
                  }}
                >
                  <LockOutlined style={{ marginRight: "8px" }} />
                  Security
                </Title>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={labelStyle}>Password</span>}
                      name="password"
                      rules={[
                        { required: true, message: "Please enter password" },
                        { min: 6, message: "Password must be at least 6 characters" },
                      ]}
                    >
                      <Input.Password
                        prefix={<LockOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                        placeholder="Enter password"
                        size="middle"
                        style={inputStyle}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={labelStyle}>Confirm Password</span>}
                      name="password2"
                      dependencies={['password']}
                      rules={[
                        { required: true, message: "Please confirm password" },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue('password') === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error('Passwords do not match'));
                          },
                        }),
                      ]}
                    >
                      <Input.Password
                        prefix={<LockOutlined style={{ color: "#470172", fontSize: "14px" }} />}
                        placeholder="Confirm password"
                        size="middle"
                        style={inputStyle}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider style={{ margin: "30px 0" }} />

                {/* Submit Button */}
                <Form.Item style={{ textAlign: "center" }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    size="large"
                    style={{
                      backgroundColor: "#672f89",
                      borderColor: "#672f89",
                      height: "50px",
                      fontSize: "16px",
                      fontWeight: 500,
                      fontFamily: "Rubik, sans-serif",
                      borderRadius: "8px",
                      width: "300px",
                    }}
                  >
                    {loading ? "Processing..." : "Register Institution"}
                  </Button>
                </Form.Item>

                <div style={{ textAlign: "center", marginTop: "20px" }}>
                  <Text style={{ ...baseStyle, fontSize: "14px", color: "#666" }}>
                    Already have an account?{" "}
                    <a
                      href="/login"
                      style={{
                        color: "#672f89",
                        fontWeight: 500,
                        textDecoration: "none",
                      }}
                    >
                      Sign in
                    </a>
                  </Text>
                </div>
              </Form>
            </Card>
          </div>
        </div>
      )}
    </>
  );
};

export default AcademiaRegister;
